import React from 'react'
import {
  Truck,
  Gauge,
  Clock,
  Globe2,
  ShieldCheck,
  CheckCircle,
  PackageSearch,
  MoveRight,
  Route,
  BarChart3
} from 'lucide-react'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import processImage from '../assets/Delivery.jpg'
import MoreServicesSection from './MoreServicesSection'
import CTA from './CTA'

const CLPage = () => (
  <main className='bg-white text-gray-800 overflow-hidden relative font-sans'>
    <Helmet>
      <title>Courier Logistics (CL) Services | S INFINITE EXPRESS</title>
      <meta
        name='description'
        content='Experience reliable, fast, and secure Courier Logistics (CL) services across India with S INFINITE EXPRESS. Perfect for businesses that demand efficiency, nationwide reach, and real-time tracking.'
      />
      <meta
        name='keywords'
        content='Courier Logistics, CL Shipping, Express Courier India, Same-day Delivery, Secure Courier, Business Courier, Fast Delivery, Real-Time Tracking, Door-to-Door Courier India, Logistics for Businesses, Corporate Courier Services, S Infinite Express, India Courier Services, B2B Courier, Timely Delivery India, Nationwide Courier Partner'
      />
      <meta name='robots' content='index, follow' />
      <link rel='canonical' href='https://sinfiniteexpress.com/services/cl' />
      <meta
        property='og:title'
        content='Courier Logistics (CL) Services | S INFINITE EXPRESS'
      />
      <meta
        property='og:description'
        content='Nationwide Courier Logistics by S INFINITE EXPRESS. Fast, secure, and trackable CL services for businesses across India.'
      />
      <meta
        property='og:url'
        content='https://sinfiniteexpress.com/services/cl'
      />
      <meta property='og:type' content='website' />
    </Helmet>

    {/* INTRO */}
    <section className='max-w-7xl mx-auto py-20 px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center'>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className='space-y-6'
      >
        <h2 className='text-3xl md:text-4xl font-bold text-[#211f57]'>
          Why Choose Our Courier Logistics?
        </h2>
        <div className='w-20 h-1 bg-[#d32c30] rounded'></div>
        <p className='text-gray-700 text-lg'>
          <span className='font-bold text-[#d32c30]'>S INFINITE EXPRESS</span>{' '}
          simplifies courier logistics with a
          <span className='text-[#211f57] font-semibold'> dedicated fleet</span>
          , real-time visibility, and committed delivery timelines. Whether
          you're sending parcels across town or critical shipments nationwide,
          our CL services ensure speed, transparency, and care.
        </p>

        <div className='bg-gradient-to-r from-[#fff44f] via-[#fff86f] to-[#fffcb0] text-black p-6 rounded-xl shadow-lg space-y-3'>
          <h3 className='text-lg font-semibold flex items-center gap-2'>
            🚀 Deliver Without Delays
          </h3>
          <p className='text-black'>
            Secure, sealed, and trackable — CL for businesses who value time.
          </p>

          <ul className='list-disc list-inside text-black text-sm space-y-1 mt-2'>
            <li>Real-time shipment tracking for full transparency</li>
            <li>Secure packaging and sealed handovers</li>
            <li>Priority handling for urgent business deliveries</li>
            <li>Pan-India coverage with reliable transit schedules</li>
          </ul>

          <a
            href='/get-a-quote'
            className='inline-flex items-center gap-2 text-white px-4 py-2 bg-[#d32c30] rounded-md text-sm font-medium hover:bg-[#b82629] transition'
          >
            Start Shipping <MoveRight className='w-4 h-4' />
          </a>
        </div>
      </motion.div>

      <motion.img
        src={processImage}
        alt='Courier Logistics Process'
        className='rounded-3xl shadow-2xl hover:scale-105 transition-transform duration-700 border-4 border-white'
        loading='lazy'
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      />
    </section>

    {/* Features */}
    <div className='py-20 bg-gradient-to-br from-[#211f57] to-[#2f2c72]'>
      <div className='max-w-7xl mx-auto px-4'>
        <h2 className='text-3xl md:text-4xl font-extrabold text-center text-[#fff44f] mb-4'>
          CL Services: Delivering Excellence
        </h2>
        <p className='text-center text-white max-w-3xl mx-auto mb-12'>
          From fast dispatch to precise tracking, discover why our Courier
          Logistics are trusted by thousands of businesses.
        </p>

        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8'>
          {[
            {
              icon: <Truck className='w-6 h-6 text-blue-600' />,
              bg: 'bg-blue-50',
              title: 'Dedicated Vehicles',
              description:
                'No load sharing—your consignments get exclusive truck space.'
            },
            {
              icon: <Gauge className='w-6 h-6 text-green-600' />,
              bg: 'bg-blue-100',
              title: 'Speed Priority',
              description:
                'Fastest routes with guaranteed pickup & delivery slots.'
            },
            {
              icon: <Clock className='w-6 h-6 text-purple-600' />,
              bg: 'bg-blue-50',
              title: 'Time-Bound Delivery',
              description: 'Precision scheduling down to the hour with SLAs.'
            },
            {
              icon: <Globe2 className='w-6 h-6 text-yellow-600' />,
              bg: 'bg-blue-100',
              title: 'Pan-India Network',
              description:
                'Covering all major cities and remote locations seamlessly.'
            },
            {
              icon: <ShieldCheck className='w-6 h-6 text-orange-600' />,
              bg: 'bg-blue-50',
              title: 'Sealed Shipments',
              description:
                'Tamper-evident seals and secure packaging for safety.'
            },
            {
              icon: <PackageSearch className='w-6 h-6 text-red-600' />,
              bg: 'bg-blue-100',
              title: 'Live Tracking & Notifications',
              description:
                'Real-time status updates via dashboard, SMS, and email.'
            },
            {
              icon: <BarChart3 className='w-6 h-6 text-teal-600' />,
              bg: 'bg-blue-50',
              title: 'Performance Analytics',
              description:
                'Detailed reports on delivery performance and trends.'
            },
            {
              icon: <Route className='w-6 h-6 text-pink-600' />,
              bg: 'bg-blue-100',
              title: 'Optimized Routing',
              description: 'AI-powered routing to cut delivery times and costs.'
            },
            {
              icon: <CheckCircle className='w-6 h-6 text-[#211f57]' />,
              bg: 'bg-blue-50',
              title: '24/7 Customer Support',
              description: 'Dedicated logistics experts ready to help any time.'
            }
          ].map((feature, index) => (
            <div
              key={index}
              className={`group ${feature.bg} p-6 rounded-xl backdrop-blur-sm shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 hover:bg-gradient-to-r from-[#fff44f] via-[#fff86f] to-[#fffcb0]`}
            >
              <div className='flex items-center justify-center mb-4'>
                <div className='w-12 h-12 rounded-full border-2 border-gray-300 flex items-center justify-center transition-colors duration-300 group-hover:bg-white'>
                  <div className='transition-colors duration-300 group-hover:text-yellow-600'>
                    {feature.icon}
                  </div>
                </div>
              </div>
              <h3 className='text-lg font-bold text-[#211f57] group-hover:text-[#211f57] mb-2 group relative inline-block'>
                {feature.title}
                <span className='absolute left-0 -bottom-1 h-0.5 w-0 bg-[#211f57] transition-all duration-300 group-hover:w-full'></span>
              </h3>

              <p className='text-sm text-gray-700 group-hover:text-[#211f57] transition-colors duration-300'>
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>

    <MoreServicesSection />
    <CTA />
  </main>
)

export default CLPage
